import { logEvent } from "@/analytics/analytics"
import { useAuthContext } from "@/authentication/AuthProvider"
import { useViewerMaybe } from "@/authentication/ViewerProvider"
import { Button } from "@/components/ui/button"
import { FieldGroup } from "@/components/ui/field-group"
import { FormActions } from "@/components/ui/form-actions"
import { RhfField } from "@/components/ui/rhf-field"
import { useToast } from "@/components/ui/use-toast"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { gql } from "@/generated"
import { rootPath, joinOrganizationSigninPath } from "@/novus/novus-paths"
import { assert } from "@/util/assert"
import { webLinks } from "@/util/environment"
import { displayValidationErrors } from "@/util/validations"
import { useMutation } from "@apollo/client"
import { zodResolver } from "@hookform/resolvers/zod"
import { HelpCircle } from "lucide-react"
import React from "react"
import {
  FormProvider,
  SubmitHandler,
  useForm,
  Controller,
} from "react-hook-form"
import { useNavigate, Link } from "react-router-dom"
import { z, ZodType } from "zod"

const CreateAccountMutation = gql(`
  mutation CreateAccount($input: CreateUserInput!){
    createUser(input: $input) {
      sessionToken
      errors {
        path
        fullMessages
      }
    }
  }
`)

type FormValues = {
  name: string
  firstName: string
  lastName: string
  email: string
  password: string
}

const schema: ZodType<FormValues> = z.object({
  name: z.string(),
  email: z
    .string()
    .min(1, "Email address is required")
    .email({ message: "Email address is invalid" }),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  password: z
    .string()
    .min(8, "A password of at least 8 characters is required"),
})

const CreateAccountForm = ({
  email,
  organizationMemberInviteToken,
}: {
  email?: string
  organizationMemberInviteToken?: string
}) => {
  const navigate = useNavigate()
  const { reloadViewer } = useViewerMaybe()
  const { setToken } = useAuthContext()
  const [execCreateUser] = useMutation(CreateAccountMutation)
  const { toast } = useToast()
  const defaultValues: FormValues = {
    name: "",
    firstName: "",
    lastName: "",
    email: email || "",
    password: "",
  }

  const methods = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues,
  })
  const { control, formState, setError } = methods

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    logEvent("Create member user")

    // Simple captcha / honeypot
    if (values.name.length > 0) return

    try {
      const { data } = await execCreateUser({
        variables: {
          input: {
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            password: values.password,
            organizationMemberInviteToken: organizationMemberInviteToken,
          },
        },
      })

      if (data?.createUser.errors && data?.createUser.errors.length > 0) {
        throw data?.createUser.errors
      }

      assert(data?.createUser?.sessionToken)
      setToken(data.createUser.sessionToken)
      await reloadViewer()
      navigate(rootPath.pattern)
    } catch (e: any) {
      console.error(e)
      displayValidationErrors({
        recordType: "user",
        errors: e,
        setError,
        toast,
        validationErrorTitle: "Account was not created.",
      })
    }
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <FieldGroup row={true}>
          <RhfField
            name="firstName"
            control={control}
            placeholder="First name"
            label="First Name"
            autoFocus={true}
          />
          <RhfField
            name="lastName"
            control={control}
            placeholder="Last name"
            label="Last Name"
          />
        </FieldGroup>
        <FieldGroup>
          <div className="flex flex-col gap-0">
            <div className="flex justify-between">
              <Label>Email Address</Label>
            </div>
            <div className="flex items-center gap-1 mb-2">
              <p className="text-sm text-gray-600">
                We recommend using your personal email address here.
              </p>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <HelpCircle size={16} />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-80 p-3"
                  align="start"
                  sideOffset={8}
                  useTriggersWidth={false}
                >
                  <p className="text-sm text-gray-700">
                    You'll be able to sign up for 529 payroll contributions, and
                    log into the Hadley app with this email address to connect
                    and save with family and friends.
                  </p>
                </PopoverContent>
              </Popover>
            </div>
            <Controller
              control={control}
              name="email"
              render={({ field: { onChange, onBlur, value } }) => (
                <Input
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  name="email"
                  placeholder="Email address"
                  disabled={!!email}
                />
              )}
            />
            {!!formState.errors.email && (
              <div className="mt-2">
                <div className="text-red-500 text-xs">
                  {typeof formState.errors.email?.message === "string"
                    ? formState.errors.email.message
                    : "Invalid input"}
                </div>
              </div>
            )}
          </div>
        </FieldGroup>
        <FieldGroup>
          <RhfField
            name="password"
            control={control}
            type="password"
            placeholder="Password"
            label="Password"
          />
        </FieldGroup>

        <RhfField name="name" control={control} hidden={true} />

        <FormActions>
          <Button
            type="submit"
            variant="default"
            disabled={formState.isSubmitting}
          >
            Create Account
          </Button>
        </FormActions>

        {organizationMemberInviteToken && (
          <p className="mt-5 text-sm text-center">
            Already have an account?{" "}
            <Link
              to={joinOrganizationSigninPath({
                memberInviteToken: organizationMemberInviteToken,
              })}
              className="underline text-primary"
            >
              Sign In
            </Link>
          </p>
        )}

        <div className="text-sm text-neutral-400 max-w-[270px] mx-auto mt-8">
          By creating an account you agree with our{" "}
          <a
            href={webLinks.privacyPolicy}
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            Privacy Policy
          </a>{" "}
          and{" "}
          <a
            href={webLinks.termsOfUse}
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            Terms of Use
          </a>
          .
        </div>
      </form>
    </FormProvider>
  )
}

export default CreateAccountForm
